import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { I18nField } from './AdminHero';
import { Plus, Save, Trash2, Eye, EyeOff, X } from 'lucide-react';
import { toast } from 'sonner';

const AdminPricing = () => {
  const { refresh } = useSiteConfig();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const r = await api.get('/admin/pricing');
    setCats(r.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateCat = (idx, patch) => setCats((a) => a.map((x, i) => i === idx ? { ...x, ...patch } : x));

  const addCat = async () => {
    const r = await api.post('/admin/pricing', {
      name: { de: 'Neue Kategorie', en: 'New Category', ar: 'فئة جديدة' },
      items: [], order: cats.length, visible: true,
    });
    setCats((a) => [...a, r.data]);
    refresh();
  };

  const saveCat = async (c) => {
    try { await api.put(`/admin/pricing/${c.id}`, c); toast.success('Saved'); refresh(); }
    catch { toast.error('Save failed'); }
  };

  const removeCat = async (c) => {
    if (!window.confirm('Delete this category and all its items?')) return;
    await api.delete(`/admin/pricing/${c.id}`);
    setCats((a) => a.filter((x) => x.id !== c.id));
    refresh();
  };

  const addItem = (idx) => {
    updateCat(idx, { items: [...(cats[idx].items || []), { name: { de: '', en: '', ar: '' }, price: '', duration: '' }] });
  };
  const updateItem = (cIdx, iIdx, patch) => {
    const items = cats[cIdx].items.map((it, i) => i === iIdx ? { ...it, ...patch } : it);
    updateCat(cIdx, { items });
  };
  const removeItem = (cIdx, iIdx) => {
    updateCat(cIdx, { items: cats[cIdx].items.filter((_, i) => i !== iIdx) });
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-[#5a1d3b]" style={{ fontWeight: 500 }}>Pricing</h1>
          <p className="text-gray-500 text-sm">Categories and price list items.</p>
        </div>
        <Button onClick={addCat} className="bg-[#c4477a] hover:bg-[#a83668] text-white rounded-full h-11 px-5 gap-2">
          <Plus className="w-4 h-4" /> Add Category
        </Button>
      </div>

      <div className="space-y-4">
        {cats.map((c, cIdx) => (
          <details key={c.id} className="bg-white border border-gray-200 rounded-xl" open={cIdx === 0}>
            <summary className="px-5 py-4 cursor-pointer flex items-center gap-3 list-none">
              <span className="flex-1 font-medium text-[#5a1d3b]">{c.name?.de || c.name?.en || 'Category'}</span>
              <span className="text-xs text-gray-500">{(c.items || []).length} items</span>
              {!c.visible && <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500">Hidden</span>}
            </summary>
            <div className="px-5 pb-5 pt-2 border-t border-gray-100 space-y-5">
              <I18nField label="Category Name" value={c.name} onChange={(v) => updateCat(cIdx, { name: v })} />

              <div>
                <Label className="mb-3 block text-sm">Items</Label>
                <div className="space-y-3">
                  {(c.items || []).map((it, iIdx) => (
                    <div key={iIdx} className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                      <div className="md:col-span-7">
                        <I18nField label="" value={it.name} onChange={(v) => updateItem(cIdx, iIdx, { name: v })} />
                      </div>
                      <div className="md:col-span-3">
                        <Label className="mb-2 block text-xs">Price</Label>
                        <Input value={it.price || ''} onChange={(e) => updateItem(cIdx, iIdx, { price: e.target.value })} placeholder="45" className="h-10" />
                      </div>
                      <div className="md:col-span-2 flex items-end justify-end h-full">
                        <Button variant="ghost" onClick={() => removeItem(cIdx, iIdx)} className="text-red-600">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="ghost" onClick={() => addItem(cIdx)} className="mt-3 text-[#c4477a]">
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>

              <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                <button onClick={() => updateCat(cIdx, { visible: !c.visible })} className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#c4477a]">
                  {c.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  {c.visible ? 'Visible' : 'Hidden'}
                </button>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => removeCat(c)} className="text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                  <Button onClick={() => saveCat(c)} className="bg-[#c4477a] hover:bg-[#a83668] text-white rounded-full px-5">
                    <Save className="w-4 h-4 mr-1.5" /> Save
                  </Button>
                </div>
              </div>
            </div>
          </details>
        ))}
        {cats.length === 0 && <p className="text-center text-gray-500 py-12">No categories yet.</p>}
      </div>
    </div>
  );
};

export default AdminPricing;
